import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  loadingCounterAtom,
  userDataAtom,
  userBranchesAtom,
  selectedBranchAtom,
  routesAtom,
  moduleFlagsAtom,
  openSnackbarAdviseAtom,
  userPermissionsAtom,
  userRolesAtom,
} from '../../Atoms';
import useAPI from '../../api';
import Loading from '../../Components/Loading';
import useMobileMode from '../../Hooks/useMobileMode';
import SnackbarAdvise from '../../Components/Snackbar';
import IncomingCallPopup from '../../Components/Popup/IncomingCallPopup';
import useAuthAPI from '../../api/auth';
import CallHubProvider from '../../Components/Hubs/CallHubProvider';
import ChatHubProvider from '../../Components/Hubs/ChatHubProvider';
import PriorityHubProvider from '../../Components/Hubs/PriorityHubProvider';
import Main from './Main';
import '../../Styles/app.scss';
import AlertDialog from '../../Components/Dialog/AlertDialog';
import keysToLowerCase from '../../Helpers/keysToLowerCase';
import getErrorMessage from '../../Helpers/Errors/getErrorMessage';
import { flattenRoutes, resolveRouteKey } from '../routes';

const POSITION_WAIT_MS = 5000;

// The browser's position, or null when it is refused, unavailable or not given within POSITION_WAIT_MS,
// including while the permission prompt is still unanswered.
const getPosition = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    const timer = setTimeout(() => resolve(null), POSITION_WAIT_MS);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        clearTimeout(timer);
        resolve(coords);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { maximumAge: 600000, timeout: POSITION_WAIT_MS }
    );
  });

const AuthenticatedLayoutPage = function AuthenticatedLayoutPage() {
  const API = useAPI();
  const intl = useIntl();
  const AuthAPI = useAuthAPI();
  const navigate = useNavigate();
  const location = useLocation();

  const mobileMode = useMobileMode();

  const loadingCounter = useAtomValue(loadingCounterAtom);
  const openSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const userRoles = useAtomValue(userRolesAtom);
  // null until the bootstrap payload lands. `Main` waits on it for the same
  // reason it waits on `selectedBranch`: a permission-gated control rendered
  // before its key arrives appears and then vanishes.
  const [permissions, setPermissions] = useAtom(userPermissionsAtom);
  const [moduleFlags, setModuleFlags] = useAtom(moduleFlagsAtom);
  const [routes, setRoutes] = useAtom(routesAtom);
  const [userData, setUserData] = useAtom(userDataAtom);
  const [userBranches, setUserBranches] = useAtom(userBranchesAtom);
  const [selectedBranch, setSelectedBranch] = useAtom(selectedBranchAtom);

  // The user is let go only once the server has answered, closed or not. Cleared any sooner, the
  // router rebuilds and asks the server whether anyone is logged in, and a session the logout has
  // not finished closing answers yes: the user lands back inside, on the default page.
  const onLogout = () => {
    AuthAPI.logout().then(() => {
      navigate('/');
      setRoutes([]);
      setUserData(null);
      setSelectedBranch(null);
      setUserBranches(null);
      // Back to "not known yet", not to empty: the next login must wait for its
      // own answer rather than briefly reading as a user who holds nothing.
      setPermissions(null);
      setModuleFlags(null);
    });
  };

  useEffect(() => {
    if (userRoles && userRoles[0]?.roleId === 'ACCOUNTING') {
      navigate('/purchaseHistory');
    }
  }, [userRoles]);

  useEffect(() => {
    if (!userData || userBranches) return undefined;

    let cancelled = false;
    // A branch chosen by hand in this session wins; otherwise the closest one is looked for.
    const sessionBranch = JSON.parse(sessionStorage.getItem('selectedBranch'));

    (sessionBranch ? Promise.resolve(null) : getPosition()).then((position) => {
      if (cancelled) return;
      API.getLoggedUserBranches(
        position,
        (result) => {
          if (result.length === 0) {
            openSnackbarAdvise({
              message: intl.messages.USER_BRANCH_NOTFOUND,
              severity: 'error',
            });
            AuthAPI.logout();
          } else {
            setUserBranches(result);
            // A saved branch that is not among the user's is ignored.
            setSelectedBranch(
              result.find(({ id }) => id === sessionBranch?.id) ||
                result.find((x) => x.isDefault)
            );
          }
        },
        (err) => {
          openSnackbarAdvise({
            message: getErrorMessage(err) || intl.messages.ERROR,
            severity: 'error',
          });
        }
      );
    });

    return () => {
      cancelled = true;
    };
  }, [userData, userBranches]);

  useEffect(() => {
    //if is index path, navigate to user's default page. If no routes are available or default page is not found, navigate to priorities.
    if (location?.pathname === '/') {
      if (routes?.length > 0) {
        const extensionData = keysToLowerCase(
          JSON.parse(userData?.extensionData || '{}')
        );
        // Match on the canonical key: the stored defaultPage is a route key,
        // while the server `name` is often a GUID.
        const currentRoute = flattenRoutes(routes).find(
          (route) => resolveRouteKey(route) === extensionData?.defaultPage
        );
        navigate(currentRoute?.target || '/priorities');
      }
    }
  }, [location, routes]);

  return (
    <>
      {selectedBranch && (
        <>
          <ChatHubProvider channel={selectedBranch?.id} />
          <CallHubProvider channel={selectedBranch?.id} />
        </>
      )}
      <PriorityHubProvider />
      {/* In mobile mode the global circular-progress backdrop is suppressed in
          favour of per-screen skeletons (see MOBILE_PLAN.md). Desktop unchanged. */}
      <Loading
        loadingCounter={loadingCounter}
        open={!mobileMode && Boolean(loadingCounter)}
      />
      <SnackbarAdvise />
      <AlertDialog />
      <IncomingCallPopup />
      {selectedBranch && permissions && moduleFlags && (
        <Main
          routesTree={routes}
          onLogout={onLogout}
          userRoles={userRoles}
          loggedUserId={userData?.id}
          selectedBranchId={selectedBranch?.id}
        />
      )}
    </>
  );
};

export default AuthenticatedLayoutPage;
